import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { FormLabel } from '../../../components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';
import { templatePreviewApi, type VorschauDatensaetze, type VorschauDatensatz } from '../../../lib/api/templates';

interface Props {
  templateKey: string;
  standortId?: number | null;
  value: VorschauDatensatz | null;
  onChange: (value: VorschauDatensatz | null) => void;
}

/**
 * Echter Datensatz für die Vorlagen-Vorschau (T-0180). Welche Art passt —
 * Anfrage, Probestunde, Anmeldung, Kündigung —, sagt der Server anhand des
 * Vorlagenschlüssels; für andere Vorlagen bleibt es bei Beispielwerten.
 */
export default function VorschauDatensatzWahl({ templateKey, standortId, value, onChange }: Props) {
  const [suche, setSuche] = useState('');
  const [daten, setDaten] = useState<VorschauDatensaetze | null>(null);

  const laden = async (q: string) => {
    if (!templateKey) return;
    try {
      const ergebnis = await templatePreviewApi.records(templateKey, q, standortId);
      setDaten(ergebnis);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Datensätze konnten nicht geladen werden');
      setDaten(null);
    }
  };

  useEffect(() => {
    setSuche('');
    onChange(null);
    void laden('');
    // Neu laden, wenn Vorlage oder Standort wechseln.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey, standortId]);

  if (!templateKey) return null;
  if (daten && !daten.art) {
    return (
      <p className="text-sm text-muted-foreground">
        Für diese Vorlage gibt es keine passenden Datensätze – die Vorschau zeigt Beispielwerte.
      </p>
    );
  }

  const art = daten?.art ?? '';
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="space-y-2 md:col-span-2">
        <FormLabel>{daten?.label ?? 'Datensatz'} einsetzen (optional)</FormLabel>
        <div className="flex gap-2">
          <Input
            uiSize="md"
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void laden(suche);
            }}
            placeholder="z. B. Mustermann"
          />
          <Button variant="outline" onClick={() => void laden(suche)}>Suchen</Button>
        </div>
      </div>
      <div className="space-y-2">
        <FormLabel>{daten?.label ?? 'Datensatz'}</FormLabel>
        <Select
          value={value && value.record_type === art ? String(value.record_id) : 'beispiel'}
          onValueChange={(v) => onChange(v === 'beispiel' ? null : { record_type: art, record_id: Number(v) })}
        >
          <SelectTrigger size="md">
            <SelectValue placeholder="Beispielwerte" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="beispiel">Beispielwerte</SelectItem>
            {(daten?.items ?? []).map((item) => (
              <SelectItem key={item.id} value={String(item.id)}>
                {item.label} – {item.detail}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
